import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import type CurrentLocationService from 'garaje/services/current-location';
import type MetricsService from 'garaje/services/metrics';
import { DEFAULT_TIMEZONE, ensurePlainJsDate, type CalendarDate } from 'garaje/utils/date-fns-tz-utilities';
import {
  DATE_RANGE_PRESETS,
  formatForQueryParam,
  parseEndDate,
  parseStartDate,
  todayAsString,
} from 'garaje/utils/date-range-helper';
import { reads } from 'macro-decorators';

import type { VisitorsInvitesGroupsIndexRouteModel } from './route';

export default class VisitorsInvitesGroupsIndexController extends Controller {
  @service declare currentLocation: CurrentLocationService;
  @service declare metrics: MetricsService;
  @service declare store: Store;

  declare model: VisitorsInvitesGroupsIndexRouteModel;

  queryParams = ['startDate', 'endDate', 'page', 'query', { selectedFlows: { as: 'visitor_types' } }];

  @tracked startDate: string | null = null;
  @tracked endDate: string | null = null;
  @tracked page = 1;
  @tracked query = '';
  @tracked selectedFlows = '';

  dateRangePresetsOrder = DATE_RANGE_PRESETS;

  @reads('model.currentLocation.timezone', DEFAULT_TIMEZONE) timezone!: string;

  get todayAsString(): string {
    return todayAsString(this.timezone);
  }

  get startDateWithDefault(): string {
    return this.startDate ?? this.todayAsString;
  }

  get endDateWithDefault(): string {
    return this.endDate ?? this.todayAsString;
  }

  get isToday(): boolean {
    return this.startDateWithDefault === this.todayAsString && this.endDateWithDefault === this.todayAsString;
  }

  get selectedDateRangeWithDefault(): string {
    return this.isToday ? 'Today' : 'Custom';
  }

  get start(): Date {
    return parseStartDate(this.startDate, this.todayAsString, this.timezone);
  }

  get end(): Date {
    return parseEndDate(this.endDate, this.todayAsString, this.timezone);
  }

  get visitorTypeFilter(): string {
    // Return the comma-separated flow IDs for filter[visitor_type]
    return this.selectedFlows;
  }

  @action
  selectDateRange(start: CalendarDate, end: CalendarDate): void {
    const startDate = ensurePlainJsDate(start, this.timezone);
    const endDate = ensurePlainJsDate(end, this.timezone);

    const startFormatted = formatForQueryParam(startDate, this.timezone);
    const endFormatted = formatForQueryParam(endDate, this.timezone);

    if (startFormatted > endFormatted) {
      return;
    }

    this.startDate = startFormatted;
    this.endDate = endFormatted;
    this.page = 1;

    this.metrics.trackEvent('Group Invites Date Range Filtered', {
      start_date: this.startDate,
      end_date: this.endDate,
      preset: this.selectedDateRangeWithDefault,
    });
  }

  @action
  selectFlows(flows: Array<Flow | GlobalFlow>): void {
    this.selectedFlows = flows.map((flow) => flow.id).join(',');
    this.page = 1;

    const flowNames = flows.map((flow) => flow.name).join(', ');
    const flowIds = flows.map((flow) => flow.id).join(',');
    this.metrics.trackEvent('Group Invites Filtered by Flow', { flow_names: flowNames, flow_ids: flowIds });
  }
}
